import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, forEach, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Languages } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import {
  LocaleObject,
  MapType,
  MapTypeDetails,
  MapTypePayload,
  createType,
  deleteType,
  mapTypes,
  updateType,
} from '../../../core/services/map'
import { uploadImage } from '../../../core/services/upload-image'
import { AlertRatio } from '@/components/AlertRatio'
import { PageTitle } from '@/components'
import { Button, Card, Checkbox, Input, Modal } from '@/components/ui'
import { cn } from '@/lib/utils'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { getTranslation } from '@/account/services/auth'
import { Icon } from '@/common/components/Icon'

interface IMapTypeContent {
  locale: string
  name: string
}

interface IMapTypesValues {
  content: IMapTypeContent[]
  logo: string
  filterLogo: string
  published: boolean
}

export interface CreateEditTypeProps {
  visible: boolean
  setVisible: (show: boolean) => void
  setItems: (items: MapType[]) => void
  item: MapTypeDetails | undefined
  page: number
  size: number
  allLanguages: Languages[]
}

export const CreateEditType = (props: CreateEditTypeProps): JSX.Element => {
  const { visible, setVisible, item, page, size, setItems, allLanguages } =
    props
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [logo, setLogo] = useState<string>('')
  const [logoName, setLogoName] = useState<string>('')
  const [filterLogo, setFilterLogo] = useState<string>('')
  const [filterLogoName, setFilterLogoName] = useState<string>('')
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [invalidPhotoLogo, setInvalidPhotoLogo] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [isErrorRatioThumb, setIsErrorRatioThumb] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const language = getTranslation()

  useEffect(() => {
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        name: '',
      }
    })
    setValue('content', temp)
    if (item) {
      if (item.pinImageUrl) {
        const url = new URL(item.pinImageUrl || '')
        const i = url.pathname.lastIndexOf('/') + 1
        const imageName = url.pathname.substring(i)
        setValue('logo', imageName)
        setLogoName(imageName)
      }
      setLogo(item.pinImageUrl)
      if (item.filterImageUrl) {
        const url2 = new URL(item.pinImageUrl)
        const j = url2.pathname.lastIndexOf('/') + 1
        const imageName2 = url2.pathname.substring(j)
        setValue('filterLogo', imageName2)
        setFilterLogoName(imageName2)
      }
      setFilterLogo(item.filterImageUrl)
      setValue('published', item.published)

      const temp: IMapTypeContent[] = []
      let index = 0
      forEach(item.localeName, (locale) => {
        const keys = Object.keys(item.localeName)
        temp.push({
          locale: keys[index],
          name: locale,
        })
        index++
      })
      if (!!temp && temp.length > 0) {
        setTimeout(() => {
          map(temp, (trans) => {
            const index = findIndex(
              allLanguages,
              (l) => l.locale === trans.locale,
            )
            if (index !== -1) {
              setValue(`content.${index}.locale`, trans.locale)
              setValue(`content.${index}.name`, trans.name)
            }
          })
        }, 100)
      }
    }
    return () => {
      reset()
    }
  }, [item, visible])

  const handleDelete = () => {
    setDeleteConfirmationModal(true)
  }

  const deleteFunc = async () => {
    try {
      if (item) {
        const { status } = await deleteType([item.id])
        const resp = await mapTypes({ page, size })
        if (status === 204 && resp.status === 200) {
          setItems(resp.data.content)
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
    handleClose()
  }

  const handleClose = () => {
    setVisible(false)
    setLogo('')
    setLogoName('')
    setFilterLogo('')
    setFilterLogoName('')
  }

  const schema = yup.object().shape({
    content: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          locale: yup.string().required('Field is required'),
          name: yup.string().required('Field is required'),
        }),
      )
      .required(),
    logo: yup.string().required(),
    filterLogo: yup.string().required(),
    published: yup.boolean().required(),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<IMapTypesValues>({
    mode: 'onChange',
    resolver: yupResolver<IMapTypesValues>(schema),
    defaultValues: {
      logo: '',
      filterLogo: '',
      published: false,
    },
  })

  const submit = async (values: IMapTypesValues) => {
    const localeName: LocaleObject = {}

    forEach(values.content, (item) => {
      localeName[item.locale] = item.name
    })

    const payload: MapTypePayload = {
      published: values.published,
      localeName: localeName,
      pinImageUrl: logo,
      filterImageUrl: filterLogo,
    }

    if (item?.id) {
      try {
        const { status } = await updateType(item.id, payload)
        const resp = await mapTypes({ page, size })
        if (status === 200 && resp.status === 200) {
          setItems(resp.data.content)
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    } else {
      try {
        const { status } = await createType(payload)
        const resp = await mapTypes({ page, size })
        if (status === 200 && resp.status === 200) {
          setItems(resp.data.content)
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    }
    handleClose()
  }

  return (
    <>
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) {
            handleClose()
          }
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Map"
              subtitle={
                item?.id ? 'Edit Location Type' : 'Add New Location Type'
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {item?.id ? (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                form="map-type-form"
                type="submit"
                variant="primary"
                disabled={isUploading || Object.keys(dirtyFields).length === 0}
                loading={isUploading}
              >
                {item?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="map-type-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <div className="text-2xl font-semibold text-monochrome-1">
            Main Info
          </div>
          <div className="flex w-full flex-col gap-2">
            {map(allLanguages, (language, index) => {
              return (
                <Card
                  className="p-4 flex flex-col space-y-4 bg-monochrome-120 border-none"
                  key={language.id}
                >
                  <FormGroup
                    label="Language"
                    labelClassName="whitespace-nowrap"
                  >
                    <Input
                      value={language.name}
                      disabled
                      className="bg-monochrome-120 border-none"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Name"
                    error={errors?.content?.[index]?.name?.message}
                  >
                    <Input
                      isInvalid={!!errors?.content?.[index]?.name?.message}
                      {...register(`content.${index}.name`)}
                      type="text"
                      placeholder="Enter Name"
                      className="bg-monochrome-5-50"
                    />
                  </FormGroup>
                </Card>
              )
            })}
          </div>
          <div className="my-4 h-px w-full bg-monochrome-120" />
          <div className="text-2xl font-semibold text-monochrome-1">Logos</div>
          <FormUploadPreview
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.logo?.message || invalidPhoto || isErrorRatio
                }
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'LOCATION_TYPE_LOGO',
                    )
                    if (data.photoUrl) {
                      setLogo(data.photoUrl)
                      setLogoName(file.name)
                      setValue('logo', file.name, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
            imageName={logoName}
            isUploading={isUploading}
            url={logo}
            onRemove={() => {
              setLogo('')
              setLogoName('')
              setValue('logo', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Logo (47X59)"
            previewLabel="Logo (47X59)"
          />
          {isErrorRatio && <AlertRatio />}
          <FormUploadPreview
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.filterLogo?.message ||
                  invalidPhotoLogo ||
                  isErrorRatioThumb
                }
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    if (file.size > 1_000_000) {
                      setInvalidPhotoLogo(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatioThumb,
                      'LOCATION_TYPE_FILTER_LOGO',
                    )
                    if (data.photoUrl) {
                      setFilterLogo(data.photoUrl)
                      setFilterLogoName(file.name)
                      setValue('filterLogo', file.name, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
            imageName={filterLogoName}
            isUploading={isUploading}
            url={filterLogo}
            onRemove={() => {
              setFilterLogo('')
              setFilterLogoName('')
              setValue('filterLogo', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Filter Logo (22x22)"
            previewLabel="Filter Logo (22x22)"
          />
          {isErrorRatioThumb && <AlertRatio />}
          <FormGroup
            label="Published"
            direction="row"
            labelClassName="whitespace-nowrap"
          >
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <Checkbox
                  className="border-monochrome-1"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Map Type'}
        deleteFunc={deleteFunc}
        deletedItem={item?.localeName?.['ro']}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
